import {
  type CountryCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

/** Country-code choices for the sign-up phone field. Thailand first — the
 * product's home market — then the rest alphabetically by English name.
 * Validation and E.164 formatting go through libphonenumber-js so national
 * trunk prefixes (Thai leading 0) and per-country lengths are handled. */
export interface PhoneCountry {
  iso2: CountryCode;
  dialCode: string;
  label: string;
}

/** Always non-empty — used as the sign-up form's default selection. */
export const DEFAULT_PHONE_COUNTRY: PhoneCountry = {
  iso2: 'TH',
  dialCode: '+66',
  label: 'Thailand (+66)',
};

export const PHONE_COUNTRIES: PhoneCountry[] = [
  DEFAULT_PHONE_COUNTRY,
  { iso2: 'AU', dialCode: '+61', label: 'Australia (+61)' },
  { iso2: 'CN', dialCode: '+86', label: 'China (+86)' },
  { iso2: 'FR', dialCode: '+33', label: 'France (+33)' },
  { iso2: 'DE', dialCode: '+49', label: 'Germany (+49)' },
  { iso2: 'HK', dialCode: '+852', label: 'Hong Kong (+852)' },
  { iso2: 'IN', dialCode: '+91', label: 'India (+91)' },
  { iso2: 'ID', dialCode: '+62', label: 'Indonesia (+62)' },
  { iso2: 'JP', dialCode: '+81', label: 'Japan (+81)' },
  { iso2: 'KR', dialCode: '+82', label: 'South Korea (+82)' },
  { iso2: 'LA', dialCode: '+856', label: 'Laos (+856)' },
  { iso2: 'MY', dialCode: '+60', label: 'Malaysia (+60)' },
  { iso2: 'MM', dialCode: '+95', label: 'Myanmar (+95)' },
  { iso2: 'NL', dialCode: '+31', label: 'Netherlands (+31)' },
  { iso2: 'NZ', dialCode: '+64', label: 'New Zealand (+64)' },
  { iso2: 'PH', dialCode: '+63', label: 'Philippines (+63)' },
  { iso2: 'SG', dialCode: '+65', label: 'Singapore (+65)' },
  { iso2: 'GB', dialCode: '+44', label: 'United Kingdom (+44)' },
  { iso2: 'US', dialCode: '+1', label: 'US / Canada (+1)' },
  { iso2: 'VN', dialCode: '+84', label: 'Vietnam (+84)' },
];

/**
 * Countries whose domestic dialling form starts with trunk `0`. The national
 * input stores significant digits only — a leading 0 is stripped as the user
 * types, and max length excludes that digit (domestic max − 1).
 */
const TRUNK_ZERO_COUNTRIES = new Set<CountryCode>([
  'TH',
  'AU',
  'FR',
  'DE',
  'IN',
  'ID',
  'JP',
  'KR',
  'LA',
  'MY',
  'MM',
  'NL',
  'NZ',
  'PH',
  'GB',
  'VN',
]);

/**
 * Max significant digits in the national input (no trunk `0`).
 * Trunk-zero countries are domestic max − 1; others keep their full length.
 */
const MAX_NATIONAL_DIGITS: Partial<Record<CountryCode, number>> = {
  TH: 9,
  AU: 9,
  CN: 11,
  FR: 9,
  DE: 14,
  HK: 8,
  IN: 10,
  ID: 12,
  JP: 10,
  KR: 10,
  LA: 10,
  MY: 10,
  MM: 10,
  NL: 9,
  NZ: 10,
  PH: 10,
  SG: 8,
  GB: 10,
  US: 10,
  VN: 10,
};

/**
 * Hyphen grouping for placeholder display (significant digits, no trunk 0).
 * Derived from common national mobile formats.
 */
const PLACEHOLDER_GROUPS: Partial<Record<CountryCode, number[]>> = {
  TH: [2, 3, 4],
  AU: [3, 3, 3],
  CN: [3, 4, 4],
  FR: [1, 2, 2, 2, 2],
  DE: [3, 4, 4],
  HK: [4, 4],
  IN: [5, 5],
  ID: [3, 3, 3],
  JP: [2, 4, 4],
  KR: [2, 4, 4],
  LA: [2, 4, 4],
  MY: [2, 3, 4],
  MM: [1, 3, 6],
  NL: [1, 4, 4],
  NZ: [2, 3, 4],
  PH: [3, 3, 4],
  SG: [4, 4],
  GB: [4, 6],
  US: [3, 3, 4],
  VN: [2, 3, 4],
};

function formatDigitsByGroups(digits: string, groups: number[]): string {
  const parts: string[] = [];
  let i = 0;
  for (const size of groups) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + size));
    i += size;
  }
  if (i < digits.length) {
    parts.push(digits.slice(i));
  }
  return parts.join('-');
}

/** Digits only — spaces, dashes and letters never reach the API. */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function usesTrunkZero(iso2: CountryCode): boolean {
  return TRUNK_ZERO_COUNTRIES.has(iso2);
}

/** Max significant digits the national input may hold for this country. */
export function maxNationalDigits(iso2: CountryCode): number {
  return MAX_NATIONAL_DIGITS[iso2] ?? 15;
}

/**
 * Digits-only national input: strip a leading trunk `0` for countries that
 * use one, then clamp to the country max. Thai `0812345678` → `812345678`.
 */
export function normalizeNationalInput(iso2: CountryCode, value: string): string {
  let digits = digitsOnly(value);
  if (usesTrunkZero(iso2)) {
    digits = digits.replace(/^0+/, '');
  }
  return digits.slice(0, maxNationalDigits(iso2));
}

/** Combines the selected country + the digits typed into a validated E.164
 * string (no spaces or dashes), or null if libphonenumber rejects the number
 * for that region. Thai `812345678` with TH becomes `+66812345678`. */
export function toE164(iso2: CountryCode, nationalNumber: string): string | null {
  const digits = normalizeNationalInput(iso2, nationalNumber);
  if (digits === '') return null;
  const parsed = parsePhoneNumberFromString(digits, iso2);
  if (!parsed?.isValid()) return null;
  return parsed.format('E.164');
}

const PHONE_PLACEHOLDERS = new Map<CountryCode, string>(
  PHONE_COUNTRIES.flatMap(({ iso2 }) => {
    const example = getExampleNumber(iso2, examples);
    if (!example) return [];
    return [[iso2, normalizeNationalInput(iso2, example.formatNational())] as const];
  }),
);

/** Countries whose libphonenumber mobile example fills the max significant length. */
const FIXED_LENGTH_COUNTRIES = new Set<CountryCode>(
  [...PHONE_PLACEHOLDERS.entries()]
    .filter(([iso2, digits]) => digits.length === maxNationalDigits(iso2))
    .map(([iso2]) => iso2),
);

/** True when the user has typed enough digits and libphonenumber accepts it. */
export function isPhoneComplete(iso2: CountryCode, nationalNumber: string): boolean {
  const digits = normalizeNationalInput(iso2, nationalNumber);
  if (digits === '') return false;
  if (FIXED_LENGTH_COUNTRIES.has(iso2) && digits.length < maxNationalDigits(iso2)) {
    return false;
  }
  return toE164(iso2, digits) !== null;
}

/** Hyphenated national number for display while typing (same pattern as placeholder). */
export function formatNationalDisplay(iso2: CountryCode, value: string): string {
  const digits = normalizeNationalInput(iso2, value);
  if (digits === '') return '';
  const groups = PLACEHOLDER_GROUPS[iso2];
  if (!groups) return digits;
  return formatDigitsByGroups(digits, groups);
}

/** Example national number for the country (no trunk 0). */
export function phonePlaceholder(iso2: CountryCode): string {
  return PHONE_PLACEHOLDERS.get(iso2) ?? '';
}

/** Hyphenated placeholder in the country's usual mobile format (no trunk 0). */
export function phonePlaceholderDisplay(iso2: CountryCode): string {
  return formatNationalDisplay(iso2, phonePlaceholder(iso2));
}

/** Prefer a known dial-picker ISO; fall back to Thailand when unknown/missing. */
export function resolvePhoneCountry(iso2?: string | null): CountryCode {
  if (iso2 && PHONE_COUNTRIES.some((c) => c.iso2 === iso2)) return iso2 as CountryCode;
  return DEFAULT_PHONE_COUNTRY.iso2;
}
